import QualityOfContact from '../enums/QualityOfContact.js';

class QualityOfContactEstimator {
    /**
     * Creates QualityOfContactEstimator instance
     * @param {?Number} exitSpeedFactor - default 1, factor which can be used in order to expect lower (<1) / higher (>1) exit speeds than MLB
     * @param {?Number} launchAngleFactor - default 1, factor which can be used in order to expect lower (<1) / higher (>1) vertical launch angles than MLB
     */
    constructor(exitSpeedFactor, launchAngleFactor) {
        if (typeof(exitSpeedFactor) === 'undefined') {
            exitSpeedFactor = 1;
        }
        else if (typeof(exitSpeedFactor) !== 'number' || exitSpeedFactor <= 0 || Number.isNaN(exitSpeedFactor)) {
            throw new TypeError('exitSpeedFactor must be an number greater than 0');
        }
        if (typeof(launchAngleFactor) === 'undefined') {
            launchAngleFactor = 1;
        }
        else if (typeof(launchAngleFactor) !== 'number' || launchAngleFactor <= 0 || Number.isNaN(launchAngleFactor)) {
            throw new TypeError('launchAngleFactor must be an number greater than 0');
        }

        this.launchAngleFactor = launchAngleFactor;
        this.exitSpeedFactor = exitSpeedFactor;

        // TODO - units-converter
        this.mpsToMph = 2.23694;
    }

    /**
     * Allows to determine quality of contact based on exit speed and vertical launch,
     * @see https://docs.google.com/document/d/1Vy6RyhEqXyqW0_5aKtMIs9jwaPFw6jztjZNUkVY_VRc/edit#heading=h.3is52rp9dyww
     * @see http://tangotiger.com/index.php/site/comments/statcast-lab-radial-woba-by-launch-anglespeed
     * @param {Float} verticalLaunch - vertical launch angle of hit, degrees, 0 === paralell to the ground
     * @param {Float} exitSpeed - speed at which baseball leaves bat after making an contact, expressed in m/s
     * @returns {String} quality of contact from Enums.QualityOfContact
     */
    estimate(verticalLaunch, exitSpeed) {
        const angle = this.getExpectedLaunchAngle(verticalLaunch);
        const speed = this.getExpectedExitSpeed(exitSpeed) * this.mpsToMph;

        // based on code shared by Tangotiger http://tangotiger.com/index.php/site/comments/statcast-lab-barrels#37
/* The code for the six categories.  This was based on 2016 data.  2017 is a bit different, so if someone wants to tweak these, by all means:

SET SpeedAngle_Code 
 = case 
  when (speed * 1.5 - angle) >= 117
  and (speed + angle) >= 124
  and speed >= 98
  and angle between 4 and 50
   then 6 -- 'Barrel'

  when (speed * 1.5 - angle) >= 111
  and (speed + angle) >= 119
  and speed >= 95
  and angle between 0 and 52
   then 5 -- 'Solid-Contact'
   
  when speed <= 59
   then 1 -- 'Poorly-Weak'
 
  when (speed * 2 - angle) >= 87
  and angle <= 41
  and (speed * 2 + angle) <= 175
  and (speed + angle * 1.3) >= 89
  and speed between 59 and 72
   then 4 -- 'Flare-or-Burner'
   
  when (speed + angle * 1.3) <= 112
  and (speed + angle * 1.55) >= 92
  and speed between 72 and 86
   then 4 -- 'Flare-or-Burner'
   
  when angle <= 20
  and (speed + angle * 2.4) >= 98
  and speed between 86 and 95
   then 4 -- 'Flare-or-Burner'
   
  when (speed - angle) >= 76
  and (speed + angle * 2.4) >= 98
  and speed >= 95
  and angle <= 30
   then 4 -- 'Flare-or-Burner'

  when (speed + angle * 2) >= 116
   then 3 -- 'Poorly-Under'

  when (speed + angle * 2) <= 116
   then 2 -- 'Poorly-Topped'

   else 0 -- 'Unclassified'
 end */

        // TODO - maybe separate classifiers for that ?
        if (this.isBarrel(speed, angle)) {
            return QualityOfContact.Barrel.key;
        }
        if (this.isSolidContact(speed, angle)) {
            return QualityOfContact.SolidContact.key;
        }
        if (this.isPoorlyWeak(speed, angle)) {
            return QualityOfContact.PoorlyWeak.key;
        }
        if (this.isFlareOrBurner(speed, angle)) {
            return QualityOfContact.FlareBurner.key;
        }
        if (this.isPoorlyUnder(speed, angle)) {
            return QualityOfContact.PoorlyUnder.key;
        }
        if (this.isPoorlyTopped(speed, angle)) {
            return QualityOfContact.PoorlyTopped.key;
        }

        return QualityOfContact.Unidentified.key;
    }

    isBarrel(speed, angle) {
        /*
        when (speed * 1.5 - angle) >= 117
        and (speed + angle) >= 124
        and speed >= 98
        and angle between 4 and 50
         then 6 -- 'Barrel'
        */

        return (
            (speed * 1.5 - angle) >= 117
            && (speed + angle) >= 124
            && speed >= 98
            && angle >= 4 && angle <= 50
        );
    }

    isSolidContact(speed, angle) {
        /* 
        when (speed * 1.5 - angle) >= 111
        and (speed + angle) >= 119
        and speed >= 95
        and angle between 0 and 52
          then 5 -- 'Solid-Contact'
        */

        return (
            (speed * 1.5 - angle) >= 111
            && (speed + angle) >= 119
            && speed >= 95
            && angle >= 0 && angle <= 52
        );
    }

    isPoorlyWeak(speed, angle) {
        /* 
        when speed <= 59
          then 1 -- 'Poorly-Weak'
        */

        return (
            speed <= 59
        );
    }

    isFlareOrBurner(speed, angle) {
        /* 
        when (speed * 2 - angle) >= 87
        and angle <= 41
        and (speed * 2 + angle) <= 175
        and (speed + angle * 1.3) >= 89
        and speed between 59 and 72
          then 4 -- 'Flare-or-Burner'

        when (speed + angle * 1.3) <= 112
        and (speed + angle * 1.55) >= 92
        and speed between 72 and 86
          then 4 -- 'Flare-or-Burner'

        when angle <= 20
        and (speed + angle * 2.4) >= 98
        and speed between 86 and 95
          then 4 -- 'Flare-or-Burner'

        when (speed - angle) >= 76
        and (speed + angle * 2.4) >= 98
        and speed >= 95
        and angle <= 30
          then 4 -- 'Flare-or-Burner'
        */

        return (
            (speed * 2 - angle) >= 87
            && angle <= 42
            && (speed * 2 + angle) <= 175
            && (speed + angle * 1.3) >= 89
            && speed >= 59 && speed <= 72
        )
        || (
            (speed + angle * 1.3) <= 112
            && (speed + angle * 1.55) >= 92
            && speed >= 72 && speed <= 86
        )
        || (
            angle <= 20
            && (speed + angle * 2.4) >= 98
            && speed >= 86 && speed <= 95
        )
        || (
            (speed - angle) >= 76
            && (speed + angle * 2.4) >= 98
            && speed >= 95
            && angle <= 30
        );
    }

    isPoorlyUnder(speed, angle) {
        /* 
        when (speed + angle * 2) >= 116
          then 3 -- 'Poorly-Under'
        */

        return (
            (speed + angle * 2) >= 116
        );
    }

    isPoorlyTopped(speed, angle) {
        /* 
        when (speed + angle * 2) <= 116
          then 2 -- 'Poorly-Topped'
        */

          return (
            (speed + angle * 2) <= 116
        );
    }

    getExpectedLaunchAngle(verticalLaunch) {
        if (typeof(verticalLaunch) !== 'number' || Number.isNaN(verticalLaunch)) {
            throw new TypeError('verticalLaunch must be an number');
        }

        return verticalLaunch / this.launchAngleFactor;
    }

    getExpectedExitSpeed(exitSpeed) {
        if (typeof(exitSpeed) !== 'number' || Number.isNaN(exitSpeed)) {
            throw new TypeError('exitSpeed must be an number');
        }

        return exitSpeed / this.exitSpeedFactor;
    }
}

export default QualityOfContactEstimator;